import React, { useEffect } from "react";
import API from "apiHelpers";
import { t } from "i18next";

import { errorHandler } from "apiHelpers/api/errorHandler";
import { useDispatch, useSelector } from "react-redux";
import { RegisterAction } from "store/actions";
import { UTILS } from "utils";
import { handleLocalStorage } from "utils/storage";

function useUpdateUserProfile({
  setUserInfoModal = () => {},
  isOpenUserInfoModal = false,
}) {
  const dispatch = useDispatch();
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);

  const [initialValues, setInitialValues] = React.useState({
    email: "",
    name: "",
    phoneNumber: "",
  });

  const [formstate, setFormState] = React.useState({
    values: {
      email: "",
      name: "",
      phoneNumber: "",
      loading: false,
      error: "",
    },
    errors: {
      email: "",
      name: "",
      phoneNumber: "",
    },
  });

  const onFinish = async () => {
    if (!validateForm()) return;
    const obj = {
      brandId: brandInfo?.brand?._id,
      name: formstate?.values?.name,
      email: formstate?.values?.email,
      phoneNumber: formstate?.values?.phoneNumber,
    };
    await updateUserProfile(
      obj,
      updateFormState,
      setUserInfoModal,
      updateFormError
    );
  };

  const updateFormState = (key, val) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [key]: val,
      },
      errors: {
        ...prevState.errors,
        [key]: "",
      },
    }));
  };

  const updateFormError = (key, val) => {
    setFormState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [key]: val,
      },
    }));
  };

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const validatePhoneNumber = (phoneNumber, countryObj) => {
    if (!phoneNumber) return false;
    
    // Remove any leading zeros from local number
    let localNumber = String(phoneNumber).replace(/^0+/, '');
    
    // If country code is available, construct full international number
    if (countryObj?.dialCode) {
      const dialCode = String(countryObj.dialCode).replace(/^\+/, '');
      const fullNumber = `+${dialCode}${localNumber}`;
      return fullNumber.match(/^\+[1-9]\d{1,14}$/);
    }
    
    // Fallback: validate local number (must start with 1-9, then 1-14 digits)
    return localNumber.match(/^[1-9]\d{1,14}$/);
  };

  const validateForm = () => {
    const errors = {};
    console.log("formstate.values", formstate.values);

    if (!brandInfo?.brand?._id) errors.name = t("Please login to continue");
    if (!formstate.values.name) errors.name = t("Please enter your name");
    if (!formstate.values.phoneNumber?.phoneNumber)
      errors.phoneNumber = t("Please enter your phone number");
    if (formstate.values.name?.trim().length < 1)
      errors.name = t("Name must be at least 1 character");
    if (formstate.values.email && !validateEmail(formstate.values.email))
      errors.email = t("Please enter a valid email address");
    if (
      formstate.values.phoneNumber?.phoneNumber &&
      !validatePhoneNumber(
        formstate.values.phoneNumber?.phoneNumber,
        formstate.values.phoneNumber?.phoneCountryObj
      )
    )
      errors.phoneNumber = t("Please enter a valid phone number");
    if (Object.keys(errors).length) {
      Object.entries(errors).forEach(([key, msg]) => updateFormError(key, msg));
      return false;
    }
    return true;
  };

  //load the values from the store
  useEffect(() => {
    if (isOpenUserInfoModal) {
      const userdata = UTILS.getCurrentUser(brandInfo?.brand?._id);
      if (userdata?.user) {
        updateFormState("name", userdata?.user?.name || "");
        updateFormState("email", userdata?.user?.email || "");
        updateFormState("phoneNumber", {
          phoneNumber: userdata?.user?.phoneNumber || "",
          phoneCountryObj: userdata?.user?.phoneCountryObj || "",
        });
        setInitialValues(userdata?.user);
      }
    }
  }, [isOpenUserInfoModal]);

  async function updateUserProfile() {
    try {
      updateFormState("loading", true);
      const data = {
        brandId: brandInfo?.brand?._id,
        name: formstate?.values?.name,
        email: formstate?.values?.email,
        phoneNumber: formstate?.values?.phoneNumber?.phoneNumber,
        countryObj:formstate?.values?.phoneNumber?.phoneCountryObj,
      };
      const response = await API.updateUserProfile(data);
      if (response?.success === 1) {
        const user = UTILS.getCurrentUser(data.brandId);
        const updatedUser = {
          ...user,
          user: {
            ...user?.user,
           ...response?.data?.user,
          },
        };
        handleLocalStorage.setItem(data.brandId, updatedUser);
        dispatch(RegisterAction.optSuccess(updatedUser));

        updateFormState("name", "");
        updateFormState("email", "");
        updateFormState("phoneNumber", "");
        return response.data;
      } else {
        const errorMsg = response?.message || response?.data;
        updateFormState("error", errorHandler(errorMsg));
      }
    } catch (error) {
      const errorMsg = error?.error?.message || error?.message;
      updateFormState("error", errorHandler(errorMsg));
    } finally {
      setUserInfoModal(false);
      updateFormState("loading", false);
    }
  }
  return {  
    initialValues,
    formstate,
    setFormState,
    onFinish,
    updateFormState,
  };
}

export default useUpdateUserProfile;
